/**
 * «Назад / вперёд» по просмотренным чатам — как в браузере: кликнули в группе номер сделки,
 * ответили в чате клиента — одной кнопкой обратно в группу и снова вперёд (владелец 22.10.26).
 * Хронология — своя у вкладки (`sessionStorage`), 50 последних чатов. Заход в чат, соседний по
 * хронологии, — шаг по ней (так и браузерные «назад»/«вперёд» не плодят повторов); любой другой —
 * новый шаг, хвост «вперёд» отрезается.
 * Спека `docs/specs/2026-10-22-chat-numbers-design.md`.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";

type Trail = { ids: number[]; pos: number; names: Record<number, string> };

const KEY = "prokat.chat-trail";
const LIMIT = 50;
const EMPTY: Trail = { ids: [], pos: -1, names: {} };

function read(): Trail {
  try {
    const t = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as Trail | null;
    return t && Array.isArray(t.ids) ? t : EMPTY;
  } catch {
    return EMPTY;
  }
}

let state = read();
const listeners = new Set<() => void>();
function set(next: Trail) {
  state = next;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Хранилище закрыто (приватное окно) — хронология живёт до перезагрузки.
  }
  listeners.forEach((f) => f());
}
const subscribe = (f: () => void) => {
  listeners.add(f);
  return () => listeners.delete(f);
};

/** Открыли чат: шаг по хронологии или новый шаг. */
export function visit(id: number) {
  const { ids, pos } = state;
  if (ids[pos] === id) return;
  if (ids[pos - 1] === id) return set({ ...state, pos: pos - 1 });
  if (ids[pos + 1] === id) return set({ ...state, pos: pos + 1 });
  const next = [...ids.slice(0, pos + 1), id].slice(-LIMIT);
  const names = Object.fromEntries(next.filter((x) => state.names[x]).map((x) => [x, state.names[x]!]));
  set({ ids: next, pos: next.length - 1, names });
}

function name(id: number, title: string) {
  if (title && state.names[id] !== title) set({ ...state, names: { ...state.names, [id]: title } });
}

/** Кнопки в шапке чата; сами отмечают заход в чат `chatId` и его название. */
export function ChatTrail({ chatId, title }: { chatId: number; title: string }) {
  const trail = useSyncExternalStore(subscribe, () => state);
  const navigate = useNavigate();
  const { search } = useLocation();
  useEffect(() => visit(chatId), [chatId]);
  useEffect(() => name(chatId, title), [chatId, title]);

  const at = trail.ids[trail.pos] === chatId ? trail.pos : -1;
  if (at < 0 || trail.ids.length < 2) return null;
  const go = (id: number | undefined) => {
    if (!id) return;
    const p = new URLSearchParams(search);
    ["serial", "dial"].forEach((k) => p.delete(k));
    navigate({ pathname: `/chats/${id}`, search: p.toString() });
  };
  const back = trail.ids[at - 1];
  const forward = trail.ids[at + 1];
  const label = (id: number | undefined, dir: string) =>
    id ? `${dir}: ${trail.names[id] || `чат ${id}`}` : dir === "Назад" ? "Раньше чатов не открывали" : "Дальше чатов нет";
  const btn =
    "grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] enabled:hover:bg-[var(--tg-hover)] enabled:hover:text-[var(--tg-text)] disabled:opacity-35";
  return (
    <div className="flex flex-none items-center" data-annot="назад и вперёд по чатам">
      <button type="button" onClick={() => go(back)} disabled={!back} title={label(back, "Назад")} aria-label="Предыдущий чат" className={btn}>
        <ChevronLeft size={22} />
      </button>
      <button type="button" onClick={() => go(forward)} disabled={!forward} title={label(forward, "Вперёд")} aria-label="Следующий чат" className={btn}>
        <ChevronRight size={22} />
      </button>
    </div>
  );
}
